import React, { useMemo, useRef, useState } from "react";
import { FiLoader, FiX, FiInfo, FiZap, FiActivity } from "react-icons/fi";
import { card, labelClass, Pills, Slider } from "./VideoStudioPanel";
import ComparePreview from "./upscale/ComparePreview";
import VideoDropZone from "./videofx/VideoDropZone";
import ResultsList from "./videofx/ResultsList";
import useVideoFx from "./videofx/useVideoFx";
import { supported } from "../video/upscale/gl";
import { upscaleVideo } from "../video/upscale/process";
import { Denoiser } from "../video/denoise/gl";
import { measureNoise } from "../video/denoise/measure";
import { LEVELS, LOOKS, LOOK_IDS, PARTS, levelFor, settingsOf, sigmaOfLook, sigmaUnit } from "../video/denoise/noise";
import { LONG_VIDEO_SECONDS, clockText, estimateBytes, problemWith, sizeText } from "../video/upscale/plan";
import { recordableFormats } from "../video/timeline";

const makeEngine = (canvas) => new Denoiser(canvas);

// AI Video Noise Reduction: removes grain, colour speckles and compression blocks from a video, keeping its edges and detail. It measures how noisy the video is and
// suggests a strength. Everything happens in the visitor's browser, on their graphics card: the video is never uploaded.
export default function VideoDenoiserTool() {
  const fx = useVideoFx();
  const { file, opened, fps, busy } = fx;
  const [formats] = useState(() => recordableFormats());
  const [format, setFormat] = useState(0);
  const [look, setLook] = useState("medium");
  const [custom, setCustom] = useState(() => ({ ...LOOKS.custom }));
  const [noise, setNoise] = useState(null);                      // null = not measured yet, else the deviation in brightness levels
  const [measuring, setMeasuring] = useState(false);
  const chosen = useRef(false);                                  // the visitor picked a look: the measured one is only a suggestion until then

  const webgl = supported();
  const level = noise === null ? null : levelFor(noise);
  const settings = useMemo(() => ({ ...settingsOf(look, custom), sigma: noise ? sigmaUnit(noise) : sigmaOfLook(look) }), [look, custom, noise]);
  const problem = opened ? problemWith({ w: opened.w, h: opened.h, seconds: opened.seconds, fps: fps || 30, bytes: file?.size || 0 }) : "";

  const take = (chosenFile) => {
    setNoise(null);
    chosen.current = false;
    fx.take(chosenFile, async (next) => {
      setMeasuring(true);
      const sigma = await measureNoise(next.video);
      setMeasuring(false);
      setNoise(sigma);
      if (!chosen.current) setLook(levelFor(sigma).look);
    });
  };

  const pick = (id) => { chosen.current = true; setLook(id); };
  const setPart = (key, value) => { chosen.current = true; setLook("custom"); setCustom((c) => ({ ...(look === "custom" ? c : { ...LOOKS[look] }), [key]: value / 100 })); };
  const shown = look === "custom" ? settingsOf("custom", custom) : settingsOf(look);

  const start = () => {
    if (!opened || problem || !formats.length) return;
    fx.run(async ({ signal, onProgress, onCanvas }) => ({
      result: await upscaleVideo({ file, size: [opened.w, opened.h], look: settings, makeEngine, bitrateScale: 1.25, mime: formats[format].mime, fps: fps || 30, onProgress, onCanvas, signal }),
      ext: formats[format].ext, look: LOOKS[look].label, extra: noise !== null ? `Noise before: ${level.label.toLowerCase()} (about ${noise.toFixed(1)} of 255)` : "",
    }));
  };

  const sizeOnDisk = opened ? estimateBytes(opened.w, opened.h, fps || 30, opened.seconds) * 1.25 : 0;

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-14">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">AI Video Noise Reduction</h2>
      <p className="text-center text-gray-400 max-w-3xl mx-auto mb-9">
        Clean up grainy, speckled or blocky video. It measures how noisy your video is, averages the still parts of the picture over time (the way professional denoisers do), smooths grain and colour speckles
        without blurring edges, and brings back the fine detail. It runs on your own graphics card: your video is never uploaded, and it is free.
      </p>

      {!webgl && <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-5 py-4 mb-6" role="alert">This browser cannot use the graphics card for video (WebGL 2 is switched off). Use a current Chrome, Edge or Firefox.</p>}
      {webgl && !formats.length && <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-5 py-4 mb-6" role="alert">This browser cannot save videos. Use a current Chrome, Edge, Firefox or Safari.</p>}

      {!opened ? <VideoDropZone reading={fx.reading} onFile={take} /> : (
        <div className="grid lg:grid-cols-[1.35fr_1fr] gap-6 items-start">
          <div className={card}>
            <div className="flex items-center justify-between gap-3 mb-4">
              <span className="text-sm text-gray-200 truncate" title={file.name}>{file.name} <span className="text-gray-500">· {opened.w}×{opened.h} · {clockText(opened.seconds)}{fps ? ` · ${fps} fps` : ""} · {sizeText(file.size)}</span></span>
              <button type="button" onClick={fx.clear} disabled={!!busy} className="text-xs text-gray-400 hover:text-red-400 inline-flex items-center gap-1 shrink-0 disabled:opacity-40"><FiX size={13} /> Another video</button>
            </div>
            {busy ? (
              <div>
                <div ref={fx.liveHost} role="img" aria-label="The video being cleaned" className="rounded-xl bg-black min-h-[200px]" />
                <p className="text-xs text-gray-500 mt-2 text-center">This is the cleaned picture, frame by frame.</p>
              </div>
            ) : (
              <ComparePreview key={opened.url} opened={opened} out={{ w: opened.w, h: opened.h }} look={settings} makeEngine={makeEngine} labels={["Original", "Cleaned"]} zooms={[1, 2, 4, 8]} />
            )}
            {!busy && <p className="text-xs text-gray-500 mt-1">Press play to see the full effect: the frame averaging needs the video to be moving through time, so a paused frame shows only part of it. Zoom in to see the grain.</p>}
          </div>

          <div className="space-y-4 lg:sticky lg:top-24">
            <div className={`${card} space-y-6`}>
              <div>
                <span className={labelClass}>1. How noisy is it?</span>
                {measuring || noise === null ? (
                  <p className="text-sm text-gray-300 flex items-center gap-2"><FiLoader className="animate-spin text-accent-light" /> Measuring the noise...</p>
                ) : noise > 0 ? (
                  <div>
                    <p className="text-sm text-gray-100 flex items-center gap-2"><FiActivity className="text-accent-light" /> <strong>{level.label}</strong> <span className="text-gray-500">(about {noise.toFixed(1)} of 255)</span></p>
                    <div className="flex gap-1 mt-2.5" aria-hidden="true">{LEVELS.map((l) => <span key={l.id} className={`h-1.5 flex-1 rounded-full ${l.id === level.id ? "bg-accent-light" : "bg-white/10"}`} />)}</div>
                    <p className="text-xs text-gray-400 mt-2.5">{level.look === look ? "The look below is the one that suits this." : `Suggested look: ${LOOKS[level.look].label}.`} Measured on a few frames of your video.</p>
                  </div>
                ) : <p className="text-sm text-gray-300">Almost no grain was found: this video is very clean, or too smooth to measure. Light settings are enough; use "Compressed video" if you can see blocks.</p>}
              </div>
              <div>
                <span className={labelClass}>2. The look</span>
                <Pills label="Look" value={look} onChange={pick} options={LOOK_IDS.map((k) => [k, LOOKS[k].label])} />
                <p className="text-xs text-gray-400 mt-2.5">{LOOKS[look].about}</p>
                <div className="space-y-3.5 mt-4">
                  {PARTS.map(([key, name, about]) => (
                    <div key={key} title={about}><Slider label={name} value={Math.round(shown[key] * 100)} min={0} max={100} step={5} suffix="%" onChange={(v) => setPart(key, v)} /></div>
                  ))}
                </div>
              </div>
              {formats.length > 1 && (
                <div>
                  <span className={labelClass}>3. File type</span>
                  <Pills label="File type" value={String(format)} onChange={(v) => setFormat(Number(v))} options={formats.map((f, i) => [String(i), f.label])} />
                </div>
              )}
            </div>

            <div className={card}>
              {problem && <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 mb-4" role="alert">{problem}</p>}
              {busy ? (
                <div>
                  <div className="h-2.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-brand-gradient transition-all" style={{ width: `${Math.round(busy.progress * 100)}%` }} /></div>
                  <p className="text-sm text-gray-200 mt-3 flex items-center gap-2"><FiLoader className="animate-spin text-accent-light" /> Cleaning... {Math.round(busy.progress * 100)}% · {clockText(busy.progress * opened.seconds)} of {clockText(opened.seconds)}</p>
                  <p className="text-xs text-gray-500 mt-1.5">Keep this tab open and visible: browsers can only record a video while it plays.</p>
                  <button type="button" onClick={() => { fx.cancelRef.current.cancelled = true; }} className="mt-4 px-5 py-2.5 rounded-full border border-white/20 hover:border-red-400 text-sm font-semibold">Stop</button>
                </div>
              ) : (
                <>
                  <button type="button" onClick={start} disabled={!webgl || !formats.length || !!problem}
                    className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-gradient text-white font-semibold shadow-lg shadow-accent/20 disabled:opacity-50 disabled:cursor-not-allowed">
                    <FiZap /> Clean the video
                  </button>
                  <ul className="text-xs text-gray-400 mt-4 space-y-1.5">
                    <li>It takes about as long as the video: <strong className="text-gray-200">{clockText(opened.seconds)}</strong>{opened.seconds > 20 ? "" : " (a short clip is quick)"}.</li>
                    <li>The new file will be about <strong className="text-gray-200">{sizeText(sizeOnDisk)}</strong>, the same size as your video. Your sound is kept as it is.</li>
                    {opened.seconds > LONG_VIDEO_SECONDS && <li className="text-amber-200">This is a long video. It is better to process it in parts.</li>}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {fx.error && <p className="text-sm text-red-200 bg-red-500/10 border border-red-500/30 rounded-xl px-5 py-4 mt-6 flex items-start gap-3" role="alert"><FiInfo className="mt-0.5 shrink-0" /> {fx.error}</p>}

      <ResultsList results={fx.results} onRemove={fx.removeResult} suffix={() => "clean"} />

      <div className="mt-10 grid md:grid-cols-3 gap-4 text-sm text-gray-400">
        <div className="rounded-xl bg-white/5 border border-navy-border p-4"><p className="font-semibold text-gray-200 mb-1">What it does</p>Grain is different in every frame, the picture is not. So still parts are averaged over time, moving parts are left alone (no ghosts), and what is left is smoothed along the edges, not across them.</div>
        <div className="rounded-xl bg-white/5 border border-navy-border p-4"><p className="font-semibold text-gray-200 mb-1">Private and free</p>The video is processed by your graphics card and never leaves your computer. There is no limit on how many videos you clean.</div>
        <div className="rounded-xl bg-white/5 border border-navy-border p-4"><p className="font-semibold text-gray-200 mb-1">Good to know</p>It cannot bring back detail that noise has destroyed, and very strong settings make skin and walls look waxy: judge the result by playing the preview. It takes as long as the video, with the tab in front.</div>
      </div>
    </div>
  );
}
